import { motion } from "framer-motion";

interface FlowArrowProps {
    className?: string;
    height: number;
    horizontalLength: number;
    direction?: "left" | "right";
    stretch?: "stem" | "drop";
    fixedSegmentLength?: number;
    strokeWidth?: number;
    arrowSize?: number;
    /** Controls whether the arrow is drawn. Animates pathLength 0→1 when true */
    visible?: boolean;
    traceDelay?: number;
    traceDuration?: number;
    onTraceComplete?: () => void;
}

const ARROWHEAD_FADE_DURATION = 0.15;

export function FlowArrow({
    className,
    height,
    horizontalLength,
    direction = "right",
    stretch = "stem",
    fixedSegmentLength = 30,
    strokeWidth = 2,
    arrowSize = 10,
    visible = true,
    traceDelay = 0,
    traceDuration = 0.8,
    onTraceComplete,
}: FlowArrowProps) {
    const width = horizontalLength + strokeWidth;

    const stretchable = height - fixedSegmentLength - arrowSize;
    const stemHeight = stretch === "stem" ? Math.max(0, stretchable) : fixedSegmentLength;
    const dropHeight = stretch === "drop" ? Math.max(0, stretchable) : fixedSegmentLength;

    const stemX = direction === "right" ? strokeWidth / 2 : width - strokeWidth / 2;
    const dropX = direction === "right" ? width - strokeWidth / 2 : strokeWidth / 2;

    const pathD = `M ${stemX},0 L ${stemX},${stemHeight} L ${dropX},${stemHeight} L ${dropX},${stemHeight + dropHeight}`;

    const tipY = stemHeight + dropHeight + arrowSize;
    const arrowHeadWidth = arrowSize * 1.2;
    const arrowPoints = `${dropX - arrowHeadWidth / 2},${stemHeight + dropHeight} ${dropX},${tipY} ${dropX + arrowHeadWidth / 2},${stemHeight + dropHeight}`;
    return (
        <svg
            className={className}
            width={width}
            height={height}
            style={{ display: "block", overflow: "visible" }}
        >
            <motion.path
                d={pathD}
                stroke="currentColor"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: visible ? 1 : 0 }}
                transition={{
                    duration: traceDuration,
                    delay: visible ? traceDelay : 0,
                    ease: "linear",
                }}
                onAnimationComplete={() => {
                    if (visible) onTraceComplete?.();
                }}
            />
            <motion.polygon
                points={arrowPoints}
                fill="currentColor"
                initial={{ opacity: 0 }}
                animate={{ opacity: visible ? 1 : 0 }}
                transition={{
                    duration: ARROWHEAD_FADE_DURATION,
                    delay: visible ? traceDelay + traceDuration : 0,
                }}
            />
        </svg>
    );
}